// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import * as React from 'react';
import { type Location, useLocation } from 'react-router';
import { Box } from '../../../lib/base';
import { type TabType, Tabs } from '../../../lib/tabs';
import * as routes from '../../../routes';
import { AccountOrLoginButton } from '../../login';
import { AppBarShell } from './AppBarShell';
import { TitleLink } from './TitleLink';

export const getButtons = (location: Location): TabType[] => [
  {
    id: 'product',
    label: 'Product',
    selected: routes.isHome(location.pathname),
    href: routes.HOME,
    requireSignIn: false,
  },
  {
    id: 'team',
    label: 'Team',
    selected: routes.isTeam(location.pathname),
    href: routes.TEAM,
    requireSignIn: false,
  },
  {
    id: 'pricing',
    label: 'Pricing',
    selected: routes.isPricing(location.pathname),
    href: routes.PRICING,
    requireSignIn: false,
  },
  {
    id: 'tray-calculator',
    label: 'Tray Calculator',
    selected: routes.isTrayCalculator(location.pathname),
    href: routes.TRAY_CALCULATOR,
    requireSignIn: false,
    groupId: 'free-tools',
  },
  {
    id: 'price-calculator',
    label: 'Price Calculator',
    selected: routes.isPriceCalculator(location.pathname),
    href: routes.PRICE_CALCULATOR,
    requireSignIn: false,
    groupId: 'free-tools',
  },
  {
    id: 'seed-density-calculator',
    label: 'Seed Density Calculator',
    selected: routes.isSeedDensityCalculator(location.pathname),
    href: routes.SEED_DENSITY_CALCULATOR,
    requireSignIn: false,
    groupId: 'free-tools',
  },
  {
    id: 'excel-tool',
    label: 'Excel Production Planner',
    selected: routes.isExcelTool(location.pathname),
    href: routes.EXCEL_TOOL,
    requireSignIn: false,
    groupId: 'free-tools',
  },
  {
    id: 'blog',
    label: 'Blog',
    selected: routes.isBlog(location.pathname),
    href: routes.BLOG,
    requireSignIn: false,
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    selected: routes.isDashboard(location.pathname),
    href: routes.DASHBOARD,
    requireSignIn: true,
  },
  {
    id: 'crops',
    label: 'Crops',
    selected: routes.isCrops(location.pathname),
    href: routes.CROPS,
    requireSignIn: true,
  },
  {
    id: 'blends',
    label: 'Blends',
    selected: routes.isBlends(location.pathname),
    href: routes.BLENDS,
    requireSignIn: true,
  },
  {
    id: 'recurring-orders',
    label: 'Recurring Orders',
    selected: routes.isRecurringOrders(location.pathname),
    href: routes.RECURRING_ORDERS,
    requireSignIn: true,
  },
  {
    id: 'orders',
    label: 'Orders',
    selected: routes.isOrders(location.pathname),
    href: routes.ORDERS,
    requireSignIn: true,
  },
  {
    id: 'recurring-tasks',
    label: 'Recurring Tasks',
    selected: routes.isRecurringTasks(location.pathname),
    href: routes.RECURRING_TASKS,
    requireSignIn: true,
  },
  {
    id: 'tasks',
    label: 'Tasks',
    selected: routes.isTasks(location.pathname),
    href: routes.TASKS,
    requireSignIn: true,
  },
  {
    id: 'recurring-plantings',
    label: 'Recurring Plantings',
    selected: routes.isRecurringPlantings(location.pathname),
    href: routes.RECURRING_PLANTINGS,
    requireSignIn: true,
  },
  {
    id: 'planting-groups',
    label: 'Plantings',
    selected: routes.isPlantings(location.pathname),
    href: routes.PLANTINGS,
    requireSignIn: true,
  },
  {
    id: 'harvests',
    label: 'Harvests',
    selected: routes.isHarvests(location.pathname),
    href: routes.HARVESTS,
    requireSignIn: true,
  },
  {
    id: 'surpluses',
    label: 'Surpluses',
    selected: routes.isSurplusesBuffers(location.pathname),
    href: routes.SURPLUSES_BUFFERS,
    requireSignIn: true,
  },
  {
    id: 'calendar',
    label: 'Calendar',
    selected: routes.isCalendar(location.pathname),
    href: routes.CALENDAR,
    requireSignIn: true,
  },
  {
    id: 'farm-report',
    label: 'Farm Report',
    selected: routes.isFarmReport(location.pathname),
    href: routes.FARM_REPORT,
    requireSignIn: true,
  },
];

export const AppBarUpMD = () => {
  const location = useLocation();
  const tabs = <Tabs tabs={getButtons(location)} />;

  return (
    <AppBarShell>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          margin: '0 auto',
        }}
      >
        <Box sx={{ flex: '2 1 0%', display: 'flex', alignItems: 'center' }}>
          <TitleLink />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 1,
            mb: 0,
            mx: 6,
            flex: '3 1 0%',
          }}
        >
          {tabs}
        </Box>
        <Box sx={{ flex: '2 1 0%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <AccountOrLoginButton />
        </Box>
      </Box>
    </AppBarShell>
  );
};
